import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Phone, MessageSquareText, Video, Mail, ShieldCheck, ArrowRight, Info } from "lucide-react";
import usePageMeta from "../lib/usePageMeta";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

/**
 * CALLACHAP — LIVE Care Portal page at /care-portal/callachap.
 * Not a demo. Not connected to Company A, BALM, or any demo settings.
 *
 * MVP wiring:
 *   • Call / Text  — visibly disabled "not yet configured" state (no dial, no hardcoded numbers).
 *   • Video        — opens production SCHEDULE_CHAPLAIN_URL (from /api/config) in a new tab.
 *   • Send Message — navigates to existing /care-portal/request-support (real production intake).
 * Future org → assigned chaplain routing will be built when real organizations onboard.
 */
export default function CallAChapCarePortalPage() {
  const [videoUrl, setVideoUrl] = useState("");
  const navigate = useNavigate();

  usePageMeta({
    title: "CALLACHAP | Care Portal",
    description:
      "Connect with a CCCHAPS chaplain by phone, text, confidential message, or available remote-support options — private, voluntary, and respectful.",
    noindex: true,
  });

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setVideoUrl(r.data.schedule_chaplain_url || "")).catch(() => {});
  }, []);

  const CARDS = [
    {
      key: "call",
      icon: Phone,
      title: "Call a Chaplain",
      body: "Reach the on-call CCCHAPS chaplain team by phone through your organization's CALLACHAP number.",
      buttonLabel: "Call Now",
      status: "unconfigured",
      image: "/images/care-portal-callachap/call.jpg",
      imageAlt: "Adult mid-conversation on a private phone call while walking outdoors",
      imagePosition: "center 30%",
    },
    {
      key: "text",
      icon: MessageSquareText,
      title: "Text a Chaplain",
      body: "Send a text and a chaplain will respond. Text is not a live-chat channel — expect a reply from the on-call chaplain team.",
      buttonLabel: "Start a Text",
      status: "unconfigured",
      image: "/images/care-portal-callachap/text.jpg",
      imageAlt: "Adult quietly reading and replying to a text message on a smartphone",
      imagePosition: "70% center",
    },
    {
      key: "video",
      icon: Video,
      title: "Remote Support",
      body: "Connect through available remote-support options when included in your organization's care arrangement.",
      buttonLabel: "Remote Support",
      // Remote Support is intentionally disabled at the UI level while we
      // work through production reliability. Backend SCHEDULE_CHAPLAIN_URL
      // configuration is deliberately left in place — this is a UI-only
      // disable, not a config change. Re-enable by restoring:
      //   status: videoUrl ? "video" : "unconfigured",
      //   href: videoUrl,
      status: "unconfigured",
      image: "/images/care-portal-callachap/remote-support.jpg",
      imageAlt: "Adult in mid-conversation on a laptop video call, focused and engaged",
      imagePosition: "65% 35%",
      noteMessage: (
        <>
          Remote Support availability depends on your organization's care arrangement. Please use{" "}
          <span className="font-semibold text-slate-700">Send Message</span> below to request chaplain support.
        </>
      ),
    },
    {
      key: "message",
      icon: Mail,
      title: "Send a Confidential Message",
      body: "Send a private support request to the CCCHAPS chaplain team. A chaplain will follow up using your preferred contact method.",
      buttonLabel: "Send Message",
      status: "message",
      image: "/images/care-portal-callachap/message.jpg",
      imageAlt: "Adult thoughtfully typing a private message at a laptop",
      imagePosition: "center 55%",
    },
  ];

  return (
    <div data-testid="callachap-care-portal-page" className="bg-white">
      {/* Hero — poster-like presence, calm palette, generous space */}
      <section
        data-testid="callachap-care-portal-hero"
        className="relative overflow-hidden bg-gradient-to-b from-[#F5F8FC] via-white to-white pt-6 md:pt-8 pb-16 md:pb-24"
        aria-labelledby="callachap-care-heading"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/care-portal"
            data-testid="callachap-care-back"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#4A6FA5] mb-10"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to Care Portal
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-8">
              <img
                src="/callachap-logo.png"
                alt="CALLACHAP"
                className="h-12 md:h-14 w-auto mb-6"
                data-testid="callachap-care-logo"
              />
              <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">
                CALLACHAP · Private Employee Support
              </p>
              <h1
                id="callachap-care-heading"
                className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900"
              >
                Connect With a Chaplain
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-700 leading-relaxed max-w-2xl">
                Chaplain support is available 24/7 on-call. Choose the way that works best for you — private, voluntary, and on your terms.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-slate-500 font-medium">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200"><Phone className="w-3 h-3 text-[#4A6FA5]" strokeWidth={2.2} /> Call</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200"><MessageSquareText className="w-3 h-3 text-[#4A6FA5]" strokeWidth={2.2} /> Text</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200"><Mail className="w-3 h-3 text-[#4A6FA5]" strokeWidth={2.2} /> Message</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200"><Video className="w-3 h-3 text-[#4A6FA5]" strokeWidth={2.2} /> Remote</span>
              </div>
            </div>
            <div className="lg:col-span-4 hidden lg:block" aria-hidden="true">
              <div className="rounded-3xl bg-gradient-to-br from-[#4A6FA5]/10 via-[#4A6FA5]/5 to-transparent p-1">
                <div className="rounded-[1.4rem] bg-white/70 backdrop-blur border border-white p-8 text-center">
                  <ShieldCheck className="w-10 h-10 text-[#4A6FA5] mx-auto" strokeWidth={1.6} />
                  <p className="mt-4 font-heading text-lg text-slate-900 tracking-tight">Private. Voluntary. Respectful.</p>
                  <p className="mt-2 text-xs text-slate-500 leading-relaxed">Employers receive aggregate, non-identifying utilization reports — not your name or a written account of your conversation.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Organization code entry — routes to /care-portal/callachap/{slug} */}
      <OrgCodeEntry />

      {/* Four prominent support cards */}
      <section className="pb-20 md:pb-28 mt-8 md:mt-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-5 md:gap-7">
            {CARDS.map((c) => (
              <CardTile
                key={c.key}
                card={c}
                onMessage={() => navigate("/care-portal/request-support")}
              />
            ))}
          </div>

          {/* Privacy section — polished panel */}
          <div
            data-testid="callachap-care-privacy"
            className="mt-16 md:mt-20 rounded-3xl border border-[#4A6FA5]/15 bg-gradient-to-br from-[#F0F4F8] via-white to-[#F0F4F8]/40 p-8 md:p-12"
          >
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white shadow-sm border border-[#4A6FA5]/20">
                  <ShieldCheck className="w-5 h-5 text-[#4A6FA5]" strokeWidth={2} aria-hidden="true" />
                </span>
                <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
                  Private. Voluntary. Respectful.
                </h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-base sm:text-lg">
                Your conversation with a CCCHAPS chaplain is confidential within applicable ethical and legal
                limits, including legally required disclosures and serious safety concerns. Employers receive
                aggregate, non-identifying utilization reports; routine reports do not include employee names
                or written accounts of confidential conversations.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function CardTile({ card, onMessage }) {
  const { icon: Icon, title, body, buttonLabel, status, href, key, noteMessage, image, imageAlt, imagePosition } = card;
  const disabled = status === "unconfigured";

  return (
    <article
      data-testid={`callachap-care-card-${key}`}
      className="group rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-[#4A6FA5]/50 transition-all flex flex-col overflow-hidden"
    >
      {image && (
        <div
          className="aspect-[4/3] w-full overflow-hidden bg-slate-100"
          data-testid={`callachap-care-card-image-${key}`}
        >
          <img
            src={image}
            alt={imageAlt || ""}
            loading="lazy"
            className="w-full h-full object-cover"
            style={imagePosition ? { objectPosition: imagePosition } : undefined}
          />
        </div>
      )}
      <div className="p-6 md:p-7 flex flex-col flex-1">
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#F0F4F8] text-[#4A6FA5] group-hover:bg-[#4A6FA5] group-hover:text-white transition-colors mb-4">
        <Icon className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
      </span>
      <h3 className="font-heading text-lg md:text-xl tracking-tight text-slate-900">{title}</h3>
      <p className="mt-2 text-sm md:text-base text-slate-600 leading-relaxed flex-1">{body}</p>
      <div className="mt-6">
        {status === "video" && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`callachap-care-btn-${key}`}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider transition-colors"
          >
            <Icon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" /> {buttonLabel}
          </a>
        )}
        {status === "message" && (
          <button
            type="button"
            onClick={onMessage}
            data-testid={`callachap-care-btn-${key}`}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider transition-colors"
          >
            <Icon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" /> {buttonLabel} <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
          </button>
        )}
        {disabled && (
          <>
            <button
              type="button"
              disabled
              aria-disabled="true"
              data-testid={`callachap-care-btn-${key}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-slate-100 text-slate-400 text-sm font-semibold uppercase tracking-wider cursor-not-allowed"
            >
              <Icon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" /> {buttonLabel}
            </button>
            <div
              data-testid={`callachap-care-note-${key}`}
              className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-2.5"
            >
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" strokeWidth={2} aria-hidden="true" />
              <p className="text-xs text-slate-600 leading-relaxed">
                {noteMessage || (
                  <>
                    Not yet configured for your organization. Please use{" "}
                    <span className="font-semibold text-slate-700">Send Message</span> below or contact your CCCHAPS chaplain team.
                  </>
                )}
              </p>
            </div>
          </>
        )}
      </div>
      </div>
    </article>
  );
}

function OrgCodeEntry() {
  const [code, setCode] = useState("");
  const nav = useNavigate();
  useEffect(() => {
    try {
      const remembered = sessionStorage.getItem("callachap.slug");
      if (remembered) setCode(remembered);
    } catch {}
  }, []);
  const go = (e) => {
    e?.preventDefault?.();
    const slug = (code || "").trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-").replace(/(^-|-$)/g, "");
    if (!slug) return;
    nav(`/care-portal/callachap/${slug}`);
  };
  return (
    <section className="pt-6 pb-2 sm:pt-8" data-testid="callachap-code-entry">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <form onSubmit={go} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Your organization</p>
            <p className="mt-0.5 text-sm text-slate-700">Enter your organization access code to view the support options available through your organization.</p>
          </div>
          <input
            data-testid="callachap-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. acme-coffee"
            className="w-full sm:w-64 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
          />
          <button data-testid="callachap-code-submit" type="submit" disabled={!code.trim()} className="px-4 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] disabled:opacity-50 text-white text-sm font-semibold uppercase tracking-wider">Continue</button>
        </form>
      </div>
    </section>
  );
}

